<template>
  <div class="step-specific-container">
    <!-- Заголовок -->
    <div class="step-header">
      <div class="step-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <rect
            x="3"
            y="3"
            width="7"
            height="7"
            rx="2"
            stroke="currentColor"
            stroke-width="1.7"
          />

          <rect
            x="14"
            y="3"
            width="7"
            height="7"
            rx="2"
            stroke="currentColor"
            stroke-width="1.7"
          />

          <rect
            x="3"
            y="14"
            width="7"
            height="7"
            rx="2"
            stroke="currentColor"
            stroke-width="1.7"
          />

          <rect
            x="14"
            y="14"
            width="7"
            height="7"
            rx="2"
            stroke="currentColor"
            stroke-width="1.7"
          />
        </svg>
      </div>

      <div class="step-heading">
        <span class="step-label">
          Категория вакансии
        </span>

        <h3 class="step-heading-title">
          Выберите специфику
        </h3>
      </div>

      <Transition name="selected-status">
        <div
          v-if="selectedSpecific"
          class="header-status"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10"
              cy="10"
              r="8"
              fill="currentColor"
            />

            <path
              d="m6.4 10.1 2.2 2.2 5-5"
              stroke="white"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>

          Выбрано
        </div>
      </Transition>
    </div>

    <!-- Описание -->
    <p class="step-description">
      Выберите направление, которое лучше всего соответствует вакансии.
      Это поможет показать её подходящим кандидатам.
    </p>

    <!-- Поиск -->
    <div class="search-wrapper">
      <div class="search-icon">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            cx="10.5"
            cy="10.5"
            r="6.5"
            stroke="currentColor"
            stroke-width="1.8"
          />

          <path
            d="m15.5 15.5 4 4"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
      </div>

      <input
        v-model="searchQuery"
        type="text"
        class="search-input"
        placeholder="Найти направление"
        autocomplete="off"
        spellcheck="false"
      />

      <button
        v-if="searchQuery"
        type="button"
        class="clear-search-button"
        aria-label="Очистить поиск"
        @click="searchQuery = ''"
      >
        <svg
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6.5 6.5 13.5 13.5M13.5 6.5 6.5 13.5"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </div>

    <!-- Список -->
    <div
      v-if="filteredSpecifics.length > 0"
      class="specific-list"
    >
      <button
        v-for="(item, index) in filteredSpecifics"
        :key="item.id"
        type="button"
        class="specific-option"
        :class="{
          selected: selectedSpecific?.id === item.id
        }"
        :style="{
          '--animation-delay': `${index * 25}ms`
        }"
        :aria-pressed="selectedSpecific?.id === item.id"
        @click="selectSpecific(item.id)"
      >
        <div class="specific-icon-wrapper">
          <img
            :src="item.icon"
            :alt="item.name"
            class="specific-icon"
          />
        </div>

        <div class="option-content">
          <span class="option-text">
            {{ item.name }}
          </span>

          <span class="option-english">
            {{ item.eng_name }}
          </span>
        </div>

        <span class="checkmark">
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="m5.5 10.2 2.8 2.8 6.2-6.2"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>

        <span class="option-shine"></span>
      </button>
    </div>

    <!-- Ничего не найдено -->
    <Transition name="empty">
      <div
        v-if="filteredSpecifics.length === 0"
        class="empty-search"
      >
        <div class="empty-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="10.5"
              cy="10.5"
              r="6.5"
              stroke="currentColor"
              stroke-width="1.7"
            />

            <path
              d="m15.5 15.5 4 4M8.5 8.5l4 4M12.5 8.5l-4 4"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>
        </div>

        <strong>Ничего не найдено</strong>

        <span>
          Попробуйте изменить поисковый запрос
        </span>

        <button
          type="button"
          @click="searchQuery = ''"
        >
          Сбросить поиск
        </button>
      </div>
    </Transition>

    <!-- Выбранная специфика -->
    <Transition name="selection-summary">
      <div
        v-if="selectedSpecific"
        class="selection-summary"
      >
        <div class="summary-icon">
          <img
            :src="selectedSpecific.icon"
            :alt="selectedSpecific.name"
          />
        </div>

        <div class="summary-content">
          <span>Выбранное направление</span>
          <strong>{{ selectedSpecific.name }}</strong>
        </div>

        <div class="summary-check">
          <svg
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="m5.5 10.2 2.8 2.8 6.2-6.2"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref
} from "vue";

import SpecificsService from "../../utils/SpecificsService";

const emit = defineEmits([
  "complete-step",
  "valid",
  "notValid"
]);

const specifics = ref(
  SpecificsService.getSpecifics()
);

const searchQuery = ref("");

const selectedSpecific = ref(
  restoreSelectedSpecific()
);

const filteredSpecifics = computed(() => {
  const query =
    searchQuery.value
      .trim()
      .toLocaleLowerCase("ru-RU");

  if (!query) {
    return specifics.value;
  }

  return specifics.value.filter(item => {
    const russianName =
      (item.name || "")
        .toLocaleLowerCase("ru-RU");

    const englishName =
      (item.eng_name || "")
        .toLocaleLowerCase("en-US");

    return (
      russianName.includes(query) ||
      englishName.includes(query)
    );
  });
});

function restoreSelectedSpecific() {
  /*
    Сначала пытаемся прочитать сохранённый объект.
  */
  try {
    const raw =
      localStorage.getItem("vacancy_specific");

    if (raw) {
      const parsed = JSON.parse(raw);

      const found =
        SpecificsService
          .getSpecifics()
          .find(item => item.id === parsed?.id);

      if (found) {
        return found;
      }
    }
  } catch (error) {
    console.warn(
      "⚠️ Не удалось прочитать vacancy_specific:",
      error
    );
  }

  /*
    Поддерживаем ранее сохранённые данные,
    где записывался только отдельный ID.
  */
  const savedID =
    localStorage.getItem(
      "vacancy_specific_id"
    );

  if (!savedID) {
    return null;
  }

  return (
    SpecificsService
      .getSpecifics()
      .find(item => item.id === savedID) ||
    null
  );
}

function checkValidity() {
  if (selectedSpecific.value) {
    emit("valid");
  } else {
    emit("notValid");
  }
}

function selectSpecific(id) {
  const found = specifics.value.find(
    item => item.id === id
  );

  if (!found) {
    console.warn(
      "❌ Специфика не найдена по id:",
      id
    );

    return;
  }

  selectedSpecific.value = found;

  /*
    Теперь сохраняем и полный объект,
    и отдельные поля для обратной совместимости.
  */
  localStorage.setItem(
    "vacancy_specific",
    JSON.stringify(found)
  );

  localStorage.setItem(
    "vacancy_specific_id",
    found.id || ""
  );

  localStorage.setItem(
    "vacancy_specific_name",
    found.name || ""
  );

  localStorage.setItem(
    "vacancy_specific_eng",
    found.eng_name || ""
  );

  console.log(
    "✅ Выбрана специфика:",
    found
  );

  emit("valid");
}

function saveSpecificAndContinue() {
  if (!selectedSpecific.value) {
    emit("notValid");

    console.warn(
      "⚠️ Специфика не выбрана"
    );

    return;
  }

  /*
    Повторно сохраняем перед переходом,
    чтобы localStorage точно был актуальным.
  */
  localStorage.setItem(
    "vacancy_specific",
    JSON.stringify(selectedSpecific.value)
  );

  emit("complete-step");
}

onMounted(() => {
  document.addEventListener(
    "save-specific",
    saveSpecificAndContinue
  );

  checkValidity();
});

onUnmounted(() => {
  document.removeEventListener(
    "save-specific",
    saveSpecificAndContinue
  );
});
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.step-specific-container {
  width: 100%;
  max-width: 760px;
  max-height: 100%;

  display: flex;
  flex-direction: column;

  margin-left: 40px;
  padding: 26px;

  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(48, 132, 255, 0.075),
      transparent 250px
    ),
    #ffffff;

  border: 1px solid rgba(30, 50, 80, 0.07);
  border-radius: 24px;

  box-shadow:
    0 1px 2px rgba(15, 23, 42, 0.02),
    0 12px 34px rgba(15, 23, 42, 0.04);

  animation: card-appearance 0.4s ease both;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 13px;

  margin-bottom: 17px;
}

.step-icon {
  width: 43px;
  height: 43px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;

  background:
    linear-gradient(
      145deg,
      #36a1ff 0%,
      #4078f3 55%,
      #625bea 100%
    );

  border-radius: 14px;

  box-shadow:
    0 8px 18px rgba(48, 126, 246, 0.23),
    inset 0 1px 0 rgba(255, 255, 255, 0.24);
}

.step-icon svg {
  width: 23px;
  height: 23px;
}

.step-heading {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.step-label {
  color: #8b94a4;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.step-heading-title {
  margin: 0;

  color: #20242c;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 18px;
  line-height: 1.25;
}

.header-status {
  display: flex;
  align-items: center;
  gap: 5px;

  flex-shrink: 0;

  color: #28a46b;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
  font-weight: 600;
}

.header-status svg {
  width: 16px;
  height: 16px;
}

.step-description {
  max-width: 680px;
  margin: 0 0 18px;

  color: #697282;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  line-height: 1.65;
}

/* Search */

.search-wrapper {
  position: relative;

  width: 100%;
  max-width: 680px;
  min-height: 48px;

  display: flex;
  align-items: center;

  margin-bottom: 15px;

  background: #f6f7f9;

  border: 1px solid #e1e4e9;
  border-radius: 14px;

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.search-wrapper:hover {
  background: #fafbfc;
  border-color: #d4d9e1;
}

.search-wrapper:focus-within {
  background: #ffffff;
  border-color: #4388f5;

  box-shadow:
    0 0 0 4px rgba(67, 136, 245, 0.11),
    0 8px 22px rgba(31, 96, 205, 0.07);

  transform: translateY(-1px);
}

.search-icon {
  width: 19px;
  height: 19px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-left: 15px;

  color: #939ba8;

  transition: color 0.2s ease;
}

.search-icon svg {
  width: 19px;
  height: 19px;
}

.search-wrapper:focus-within .search-icon {
  color: #4388f5;
}

.search-input {
  min-width: 0;
  width: 100%;
  height: 46px;

  padding: 0 11px;

  color: #252b35;
  caret-color: #4388f5;

  background: transparent;
  border: none;
  outline: none;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
}

.search-input::placeholder {
  color: #a4abb6;
}

.clear-search-button {
  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  margin-right: 10px;
  padding: 0;

  cursor: pointer;

  color: #89919d;
  background: #e9ecf0;

  border: none;
  border-radius: 8px;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}

.clear-search-button:hover {
  color: #ffffff;
  background: #df555a;

  transform: scale(1.05);
}

.clear-search-button svg {
  width: 15px;
  height: 15px;
}

/* List */

.specific-list {
  width: 100%;
  max-width: 680px;
  max-height: 560px;

  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;

  overflow-y: auto;
  overflow-x: hidden;

  padding: 1px 5px 1px 1px;

  scrollbar-width: thin;
  scrollbar-color: #c7ced8 transparent;
}

.specific-list::-webkit-scrollbar {
  width: 5px;
}

.specific-list::-webkit-scrollbar-track {
  background: transparent;
}

.specific-list::-webkit-scrollbar-thumb {
  background: #c7ced8;
  border-radius: 999px;
}

.specific-option {
  --animation-delay: 0ms;

  position: relative;

  width: 100%;
  min-height: 65px;

  display: flex;
  align-items: center;
  gap: 10px;

  overflow: hidden;

  padding: 9px 10px;

  cursor: pointer;
  text-align: left;

  color: #39414d;
  background: #f8f9fb;

  border: 1px solid #e7e9ed;
  border-radius: 14px;
  outline: none;

  opacity: 0;

  animation:
    option-appearance 0.35s ease forwards;

  animation-delay: var(--animation-delay);

  transition:
    color 0.22s ease,
    background-color 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;
}

.specific-option:hover {
  background: #ffffff;
  border-color: rgba(55, 132, 237, 0.22);

  box-shadow:
    0 7px 17px rgba(35, 95, 180, 0.07);

  transform: translateY(-2px);
}

.specific-option:focus-visible {
  box-shadow:
    0 0 0 4px rgba(67, 136, 245, 0.13);
}

.specific-option.selected {
  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3795f5 0%,
      #3479e5 55%,
      #4963d9 100%
    );

  border-color: transparent;

  box-shadow:
    0 8px 19px rgba(46, 111, 220, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.16);

  transform: translateY(-1px);
}

.specific-icon-wrapper {
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  background: #ffffff;

  border: 1px solid rgba(25, 45, 70, 0.055);
  border-radius: 12px;

  box-shadow:
    0 4px 10px rgba(20, 40, 70, 0.04);

  transition:
    background-color 0.22s ease,
    border-color 0.22s ease,
    transform 0.22s ease;
}

.specific-option:hover .specific-icon-wrapper {
  transform: scale(1.04);
}

.specific-option.selected .specific-icon-wrapper {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.14);

  box-shadow: none;
}

.specific-icon {
  width: 24px;
  height: 24px;

  object-fit: contain;
}

.option-content {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
}

.option-text {
  overflow: hidden;
  text-overflow: ellipsis;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
  line-height: 1.3;
}

.option-english {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  color: #939aa6;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
  line-height: 1.3;

  transition: color 0.2s ease;
}

.specific-option.selected .option-english {
  color: rgba(255, 255, 255, 0.7);
}

.checkmark {
  width: 23px;
  height: 23px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;
  background: rgba(255, 255, 255, 0.17);

  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 50%;

  opacity: 0;
  transform: scale(0.45);

  transition:
    opacity 0.22s ease,
    transform 0.28s
      cubic-bezier(0.34, 1.56, 0.64, 1);
}

.specific-option.selected .checkmark {
  opacity: 1;
  transform: scale(1);
}

.checkmark svg {
  width: 14px;
  height: 14px;
}

.option-shine {
  position: absolute;
  top: -60%;
  left: -60%;

  width: 30%;
  height: 220%;

  pointer-events: none;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.17),
      transparent
    );

  transform: rotate(20deg);

  transition: left 0.65s ease;
}

.specific-option.selected:hover .option-shine {
  left: 130%;
}

/* Summary */

.selection-summary {
  width: 100%;
  max-width: 680px;

  display: flex;
  align-items: center;
  gap: 10px;

  margin-top: 15px;
  padding: 10px 12px;

  color: #3c7652;
  background: rgba(43, 160, 97, 0.065);

  border: 1px solid rgba(43, 160, 97, 0.12);
  border-radius: 13px;
}

.summary-icon {
  width: 34px;
  height: 34px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  background: #ffffff;
  border-radius: 10px;
}

.summary-icon img {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.summary-content {
  min-width: 0;

  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
}

.summary-content span {
  color: #7f9185;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 8px;
}

.summary-content strong {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 10px;
}

.summary-check {
  width: 26px;
  height: 26px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;
  background: #2da466;

  border-radius: 50%;
}

.summary-check svg {
  width: 15px;
  height: 15px;
}

/* Empty */

.empty-search {
  width: 100%;
  max-width: 680px;
  min-height: 230px;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  padding: 25px;

  text-align: center;
  background: #f8f9fb;

  border: 1px dashed #d8dde5;
  border-radius: 17px;
}

.empty-icon {
  width: 46px;
  height: 46px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 12px;

  color: #8ca0bc;
  background: #eef2f7;

  border-radius: 14px;
}

.empty-icon svg {
  width: 24px;
  height: 24px;
}

.empty-search strong {
  margin-bottom: 4px;

  color: #414954;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 11px;
}

.empty-search > span {
  color: #929aa7;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 9px;
}

.empty-search button {
  margin-top: 13px;
  padding: 7px 10px;

  cursor: pointer;

  color: #397edb;
  background: rgba(57, 126, 219, 0.07);

  border: 1px solid rgba(57, 126, 219, 0.1);
  border-radius: 9px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
}

/* Animations */

@keyframes card-appearance {
  from {
    opacity: 0;
    transform: translateY(7px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes option-appearance {
  from {
    opacity: 0;
    transform: translateY(5px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.selected-status-enter-active,
.selected-status-leave-active,
.selection-summary-enter-active,
.selection-summary-leave-active,
.empty-enter-active,
.empty-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.selected-status-enter-from,
.selected-status-leave-to {
  opacity: 0;
  transform: translateX(5px);
}

.selection-summary-enter-from {
  opacity: 0;
  transform: translateY(6px);
}

.selection-summary-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.empty-enter-from,
.empty-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

/* Dark theme */

html[data-theme="dark"] .step-specific-container {
  background:
    radial-gradient(
      circle at 100% 0%,
      rgba(54, 132, 255, 0.09),
      transparent 250px
    ),
    #1c1c1f;

  border-color: rgba(255, 255, 255, 0.07);

  box-shadow:
    0 14px 38px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .step-heading-title {
  color: #f1f2f4;
}

html[data-theme="dark"] .step-label {
  color: #777f8d;
}

html[data-theme="dark"] .step-description {
  color: #a5a9b2;
}

html[data-theme="dark"] .search-wrapper {
  background: #28282c;
  border-color: #3b3b41;
}

html[data-theme="dark"] .search-wrapper:hover {
  background: #2b2b30;
  border-color: #484850;
}

html[data-theme="dark"] .search-wrapper:focus-within {
  background: #2d2d32;
  border-color: #548ff1;

  box-shadow:
    0 0 0 4px rgba(78, 143, 245, 0.13),
    0 9px 24px rgba(0, 0, 0, 0.14);
}

html[data-theme="dark"] .search-input {
  color: #f1f2f4;
}

html[data-theme="dark"] .search-input::placeholder {
  color: #6f747e;
}

html[data-theme="dark"] .clear-search-button {
  color: #9ba1ab;
  background: #3c3d43;
}

html[data-theme="dark"] .specific-list {
  scrollbar-color: #4b4d54 transparent;
}

html[data-theme="dark"]
  .specific-list::-webkit-scrollbar-thumb {
  background: #4b4d54;
}

html[data-theme="dark"] .specific-option {
  color: #d8dae0;
  background: #28282c;
  border-color: #3a3b41;
}

html[data-theme="dark"] .specific-option:hover {
  background: #2d2d32;
  border-color: rgba(80, 147, 242, 0.26);

  box-shadow:
    0 8px 19px rgba(0, 0, 0, 0.16);
}

html[data-theme="dark"] .specific-option.selected {
  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #327fdc,
      #3c66d1
    );

  border-color: transparent;
}

html[data-theme="dark"] .specific-icon-wrapper {
  background: #33343a;
  border-color: rgba(255, 255, 255, 0.055);

  box-shadow:
    0 5px 11px rgba(0, 0, 0, 0.12);
}

html[data-theme="dark"]
  .specific-option.selected
  .specific-icon-wrapper {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.14);
}

html[data-theme="dark"] .option-english {
  color: #7d8490;
}

html[data-theme="dark"] .selection-summary {
  color: #82c69b;
  background: rgba(43, 160, 97, 0.075);
  border-color: rgba(43, 160, 97, 0.13);
}

html[data-theme="dark"] .summary-icon {
  background: #303136;
}

html[data-theme="dark"] .summary-content span {
  color: #7d8d82;
}

html[data-theme="dark"] .empty-search {
  background: #27272b;
  border-color: #3e4047;
}

html[data-theme="dark"] .empty-icon {
  color: #879bb6;
  background: #303239;
}

html[data-theme="dark"] .empty-search strong {
  color: #dcdee3;
}

html[data-theme="dark"] .empty-search > span {
  color: #7b828e;
}

/* Mobile */

@media (max-width: 600px) {
  .step-specific-container {
    width: 100%;
    max-width: none;

    margin-left: 0;
    padding: 20px 16px;

    border-radius: 18px;
  }

  .step-icon {
    width: 39px;
    height: 39px;
    border-radius: 12px;
  }

  .step-heading-title {
    font-size: 16px;
  }

  .header-status {
    display: none;
  }

  .step-description {
    font-size: 12px;
    line-height: 1.55;
  }

  .specific-list {
    max-height: calc(100dvh - 390px);

    grid-template-columns: 1fr;
    gap: 8px;
  }

  .specific-option {
    min-height: 61px;
  }

  .option-text {
    white-space: normal;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-specific-container,
  .specific-option {
    animation: none;
    opacity: 1;
  }

  .search-wrapper,
  .search-icon,
  .clear-search-button,
  .specific-option,
  .specific-icon-wrapper,
  .checkmark,
  .option-shine,
  .selected-status-enter-active,
  .selected-status-leave-active,
  .selection-summary-enter-active,
  .selection-summary-leave-active,
  .empty-enter-active,
  .empty-leave-active {
    transition: none;
  }
}
</style>